import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAccountPositionStore } from '../../Store/accountPositionStore';
import { useDemoAccountStore } from '../../Store/demoAccountStore';
import { modalStyle, headerStyle, footerStyle } from '../OrderEntry/styles';
import { useAuth } from '../../Context/AuthProvider';
import ConfirmCusipDeleteDialog from './ConfirmCusipDeleteDialog';
import { deleteAccountPostionMapping, checkAccountIdentifier, updateAccountOnly } from '../../http/api'; // ADD updateAccountOnly import
import AddAccountFormContent from './AddAccountFormContent';
import useSnackbar from '../../Utils/useSnackbar';
import { tokens, withAlpha } from '../../Common/designTokens';

const MAX_QTY = 1000000000;
const EMPTY_ROW = {
  cusip: '',
  secMasterId: null,
  bondInventoryId: null,
  quantity: '',
  price: '',
  oldPrice: '',
  pendingDelete: false
};
const mapApiPositionToRow = (position = {}) => ({
  cusip: position.cusip || '',
  cusipRef: position.cusipRef || position.cusip || '', // preserve API-provided canonical cusip if present
  secMasterId: position.secMasterId || null,
  bondInventoryId: position.bondInventoryId || null,
  quantity: position.quantity ?? position.quantity === 0 ? String(position.quantity) : '',
  price: position.price ?? position.price === 0 ? String(position.price) : '',
  oldPrice: position.price ?? position.price === 0 ? String(position.price) : '',
  accountPositionMappingId: position.accountPositionMappingId || null,
  pendingDelete: false
});

const AddAccountModal = ({ 
  open, 
  editMode = false, 
  editAccount = null, 
  lockHeaderFields = false, 
  onClose, 
  hidePositions = false, 
  lockAccountFieldOnly = false,
  modalTitle 
}) => {
  const { 
    setAddAccountModalOpen, 
    setEditAccountModalOpen,
    addAccount,
    updateAccount,
    setSelectedAccount
  } = useAccountPositionStore();
  const { token, userId } = useAuth();
  const { showSuccess } = useSnackbar();
  
  // Use the demoAccountStore
  const {
    accountIdentifiers,
    custodians,
    formData,
    fieldErrors,
    isLoading,
    error,
    success,
    fetchCustodians,
    updateFormData,
    updateRowData,
    addRow,
    removeRow,
    validateCusip,
    createDemoAccount,
    updateDemoAccount,
    resetFormData,
    setFieldErrors,
    clearFieldError,
    setAccountIdentifiers,
    clearError,
    clearSuccess,
    // New identifier status helpers
    accountIdentifierStatus,
    setAccountIdentifierStatus,
    clearAccountIdentifierStatus
  } = useDemoAccountStore();

  // State management
  const [rowLoadingStates, setRowLoadingStates] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState(''); // For persistent error display
  const [infoMessage, setInfoMessage] = useState(''); // For persistent info display
  const [originalPositions, setOriginalPositions] = useState([]); // Track original positions for edit mode
  const [originalCusips, setOriginalCusips] = useState(new Set()); // Track original CUSIPs for edit mode

  // Add missing refs/state for account identifier debounce/check
  const accountCheckTimeout = useRef(null);
  const [isCheckingAccount, setIsCheckingAccount] = useState(false);

  // State for CUSIP deletion confirmation
  const [cusipDeleteConfirmOpen, setCusipDeleteConfirmOpen] = useState(false);
  const [cusipToDelete, setCusipToDelete] = useState(null);
  const [cusipIndexToDelete, setCusipIndexToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Tracks the whole save sequence (sequential pending-delete calls + account update),
  // which the store-level isLoading does not cover
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSaving = isLoading || isSubmitting;

  // State for validation and tracking
  const [duplicateCusips, setDuplicateCusips] = useState(new Set());
  const [rowFieldErrors, setRowFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [touchedRowFields, setTouchedRowFields] = useState({});

  // Add state to track original form data for comparison
  const [originalFormData, setOriginalFormData] = useState(null);

  const isAccountIdentifierValidated =
    editMode || accountIdentifierStatus === 'new' || accountIdentifierStatus === 'map';

  // Treat "map" flow as an intermediate edit-like mode where account field stays editable
  const mappingMode = !editMode && accountIdentifierStatus === 'map';
  const effectiveEdit = editMode || mappingMode;

  const applyMappedAccountDetails = (details) => {
    if (!details || !details.accountInfo) return;
    const mappedRows = Array.isArray(details.data?.content)
      ? details.data.content.map(mapApiPositionToRow)
      : [];
    updateFormData({
      accountId: details.accountInfo.accountMstId || '',
      description: details.accountInfo.accountDescription || '',
      custodian: details.accountInfo.custodianName || '',
      custodianId: details.accountInfo.custodianId || '',
      rows: mappedRows.length ? mappedRows : [{ ...EMPTY_ROW }]
    });
    setRowErrors({});
    setRowFieldErrors({});
    setTouchedRowFields({});
    setDuplicateCusips(new Set());

    // Capture original state for change detection in mapping mode (same as edit)
    setOriginalPositions(
      mappedRows.map(r => ({
        cusip: r.cusip,
        accountPositionMappingId: r.accountPositionMappingId || null
      }))
    );
    setOriginalCusips(new Set(mappedRows.map(r => r.cusip)));

    setOriginalFormData({
      userId: userId,
      userName: formData.userName || '',
      accountGroup: formData.accountGroup, // suffix only (no ACC-)
      accountId: details.accountInfo.accountMstId || '',
      custodian: details.accountInfo.custodianName || '',
      custodianId: details.accountInfo.custodianId || '',
      description: details.accountInfo.accountDescription || '',
      rows: JSON.parse(JSON.stringify(mappedRows))
    });
  };

  // Handle success messages with snackbar
  useEffect(() => {
    if (success) {
      showSuccess(success);
      // Clear success message from store after showing in snackbar
      clearSuccess();
    }
  }, [success, showSuccess, clearSuccess]);

  // Handle error messages - keep them persistent
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
  }, [error]);

  // Fetch PM users, custodians when component mounts
  useEffect(() => {
    if (open && token) {
      fetchCustodians(token);
      
      // Clear errors when opening the modal
      clearError();
      clearSuccess();
      setRowErrors({});
      setRowFieldErrors({});
      setDuplicateCusips(new Set());
      setTouchedFields({});
      setTouchedRowFields({});
      setErrorMessage('');
    }
  }, [open, token, fetchCustodians, clearError, clearSuccess]);

  // Cleanup effect when modal closes
  useEffect(() => {
    if (!open) {
      // Clear all local state when modal closes
      setRowLoadingStates({});
      setRowErrors({});
      setRowFieldErrors({});
      setDuplicateCusips(new Set());
      setTouchedFields({});
      setTouchedRowFields({});
      setErrorMessage('');
      setInfoMessage('');
      setOriginalPositions([]);
      setCusipDeleteConfirmOpen(false);
      setCusipToDelete(null);
      setCusipIndexToDelete(null);
      setIsDeleting(false);
      // clear any pending account identifier debounce
      if (accountCheckTimeout.current) {
        clearTimeout(accountCheckTimeout.current);
        accountCheckTimeout.current = null;
      }
      clearAccountIdentifierStatus();
    }
  }, [open]);

  // Setup for edit mode
  useEffect(() => {
    if (editMode && editAccount && open) {
      // Store original positions for comparison in edit mode
      const originalPos = editAccount.positions || [];
      setOriginalPositions(originalPos.map(pos => ({
        cusip: pos.cusip,
        accountPositionMappingId: pos.accountPositionMappingId || null
      })));
      
      // Store original CUSIPs in a Set for easy lookup
      setOriginalCusips(new Set(originalPos.map(pos => pos.cusip)));
      
      // For edit mode, extract the account identifier without ACC- prefix for the form
      let accountGroupValue = editAccount.accountIdentifier || '';
      if (accountGroupValue.startsWith('ACC-')) {
        accountGroupValue = accountGroupValue.substring(4); // Remove "ACC-" prefix for UI
      }
      
      // Create a copy of the initial form data to track changes
      const initialFormData = {
        // pmId: editAccount.pmId,
        userId: editAccount.userId,
        userName: editAccount.userName || '', // keyname is pmname, its actually userName
        accountGroup: accountGroupValue,
        accountId: editAccount.accountId,
        custodian: editAccount.custodianName || '',
        custodianId: editAccount.custodianId,
        description: editAccount.description,
        rows: []
      };
      
      // Populate form data for edit mode
      updateFormData({
        // pmId: editAccount.pmId,
        userName: editAccount.userName || '',
        accountGroup: accountGroupValue,
        accountId: editAccount.accountId,
        custodian: editAccount.custodianName || '',
        custodianId: editAccount.custodianId,
        description: editAccount.description
      });
      
      // Populate positions if they exist
      if (editAccount.positions && editAccount.positions.length > 0) {
        const rows = editAccount.positions.map(pos => ({
          cusip: pos.cusip,
          secMasterId: pos.secMasterId,
          bondInventoryId: pos.bondInventoryId,
          quantity: pos.quantity.toString(),
          price: pos.price.toString(),
          oldPrice: pos.price.toString(), // Store original price for tracking changes
          accountPositionMappingId: pos.accountPositionMappingId || null
        }));
        updateFormData({ rows });
        
        // Store original rows for comparison
        initialFormData.rows = JSON.parse(JSON.stringify(rows));
      }
      
      // Save the initial form data
      setOriginalFormData(initialFormData);
      
    } else if (!editMode && open) {
      // Clear account group for create mode
      updateFormData({ accountGroup: '', accountId: '' });
      setOriginalFormData(null);
    }
  }, [editMode, editAccount, open, updateFormData]);

  // Check for duplicate CUSIPs whenever rows change
  useEffect(() => {
    if (formData.rows) {
      // Only consider rows NOT marked for deletion
      const activeRows = formData.rows.filter(row => !row.pendingDelete);
      const cusips = activeRows.map(row => row.cusip ? row.cusip.toUpperCase() : '').filter(cusip => cusip);
      const duplicates = cusips.filter((cusip, index, self) => 
        cusip && self.indexOf(cusip) !== index
      );
      setDuplicateCusips(new Set(duplicates));
    }
  }, [formData.rows]);

  // Field touch tracking functions
  const markFieldAsTouched = (fieldName) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  const markRowFieldAsTouched = (rowIndex, fieldName) => {
    setTouchedRowFields(prev => ({
      ...prev,
      [rowIndex]: {
        ...(prev[rowIndex] || {}),
        [fieldName]: true
      }
    }));
  };

  const handleClose = () => {
    if (editMode) {
      setEditAccountModalOpen(false, null);
    } else {
      setAddAccountModalOpen(false);
    }
    resetFormData();
    setRowLoadingStates({});
    setRowErrors({});
    setRowFieldErrors({});
    setDuplicateCusips(new Set());
    setTouchedFields({});
    setTouchedRowFields({});
    setErrorMessage('');
    setInfoMessage('');
    clearAccountIdentifierStatus();
    if (onClose) onClose();
  };

  // Form field handlers
  // const handlePMChange = (event) => {
  //   const pmId = event.target.value;
  //   const selectedPM = pmUsers.find(pm => pm.userId === pmId);
    
  //   updateFormData({
  //     pmId,
  //     pm: selectedPM ? selectedPM.userName : ''
  //   });
    
  //   markFieldAsTouched('pmId');
    
  //   if (pmId) {
  //     clearFieldError('pmId');
  //     // Only fetch accounts in create mode
  //     if (!editMode) {
  //       fetchPMAccounts(token, pmId);
  //     }
  //   }
  // };

  const handleAccountChange = (event) => {
    // Clear account-related messages when user modifies input
    clearMessage('account-check');
    
    const userInput = event.target.value;
    const shouldResetPrefill = !editMode && accountIdentifierStatus === 'map';
    const updatedFields = {
      accountGroup: userInput,
      accountId: editMode ? formData.accountId : '0'
    };

    if (shouldResetPrefill) {
      updatedFields.accountId = '0';
      updatedFields.description = '';
      updatedFields.custodian = '';
      updatedFields.custodianId = '';
      updatedFields.rows = [{ ...EMPTY_ROW }];
      setRowErrors({});
      setRowFieldErrors({});
      setTouchedRowFields({});
      setDuplicateCusips(new Set());
    }

    updateFormData(updatedFields);
    markFieldAsTouched('accountGroup');

    if (!editMode && accountIdentifierStatus) {
      clearAccountIdentifierStatus();
    }

    if (accountCheckTimeout.current) {
      clearTimeout(accountCheckTimeout.current);
      accountCheckTimeout.current = null;
    }

    if (!editMode && userInput && userInput.trim().length >= 6) {
      accountCheckTimeout.current = setTimeout(async () => {
        setIsCheckingAccount(true);
        try {
          const identifier = `ACC-${userInput}`;
          const result = await checkAccountIdentifier(token, identifier);
          if (result) {
            if (result.success === false) {
              // Handle any error response generically
              setFieldErrors(prev => ({ ...(prev || {}), accountGroup: result.message }));
              setMessage('error', result.message, 'account-check');
              setAccountIdentifierStatus('owned', result);
            } else if (result.success === true) {
              const details = result['account-details'];
              if (Array.isArray(details)) {
                clearFieldError('accountGroup');
                setAccountIdentifierStatus('new', result);
              } else if (details && details.accountInfo && details.accountInfo.accountMstId) {
                clearFieldError('accountGroup');
                // Show any info message from API response
                if (result.message) {
                  setMessage('info', result.message, 'account-check');
                }
                setAccountIdentifierStatus('map', result);
                applyMappedAccountDetails(details);
              } else {
                clearFieldError('accountGroup');
                setAccountIdentifierStatus('new', result);
              }
            }
          }
        } catch (err) {
          setFieldErrors(prev => ({ ...(prev || {}), accountGroup: err.message || 'Error checking account identifier' }));
          setMessage('error', err.message || 'Error checking account identifier', 'account-check');
          clearAccountIdentifierStatus();
        } finally {
          setIsCheckingAccount(false);
          accountCheckTimeout.current = null;
        }
      }, 500);
    } else if (!userInput || userInput.trim().length < 6) {
      clearFieldError('accountGroup');
      clearAccountIdentifierStatus();
    }

    if (userInput && userInput.trim().length >= 3) {
      clearFieldError('accountGroup');
    }
  };

  const handleCustodianChange = (event) => {
    // Clear form-related messages when user makes changes
    clearMessage('form-validation');
    
    const custodianId = event.target.value;
    const selectedCustodian = custodians.find(c => c.custodianMstId === custodianId);
    
    updateFormData({
      custodianId,
      custodian: selectedCustodian ? selectedCustodian.custodianName : ''
    });
    
    markFieldAsTouched('custodianId');
    
    if (custodianId) {
      clearFieldError('custodianId');
    }
  };

  const handleChange = (event) => {
    // Clear form-related messages when user makes changes
    clearMessage('form-validation');
    
    const { name, value } = event.target;
    
    updateFormData({
      [name]: value
    });
    
    markFieldAsTouched(name);
    
    if (value.trim() !== '') {
      clearFieldError(name);
    }
  };

  // Row handling functions
  const handleRowChange = async (index, field, value) => {
    // Clear form-related messages when user makes changes to rows
    clearMessage('form-validation');
    
    let isValid = true;
    const currentRow = formData.rows?.[index] || {};

    // Apply validation
    if (field === 'quantity' && value > MAX_QTY) isValid = false;
    if (field === 'price' && value > 150) isValid = false;

    // Mark this row field as touched
    markRowFieldAsTouched(index, field);

    if (isValid) {
      // When user clears the cusip input, also clear any cusipRef stored for that row
      if (field === 'cusip' && (!value || value.trim() === '')) {
        updateRowData(index, { cusip: '', cusipRef: '', secMasterId: null, bondInventoryId: null });
      } else {
        updateRowData(index, { [field]: value });
      }
      
      // Clear field-specific error when user makes changes
      if (rowFieldErrors[index] && rowFieldErrors[index][field]) {
        setRowFieldErrors(prev => ({
          ...prev,
          [index]: {
            ...prev[index],
            [field]: null
          }
        }));
      }
      
      // Handle CUSIP field changes
      if (field === 'cusip') {
        // Clear CUSIP-related errors when user changes input
        setRowErrors(prev => ({
          ...prev,
          [index]: null
        }));
        
        // If we already have a stored cusipRef for this row and it matches the typed value
        // (case-insensitive), set the canonical cusip and skip re-validating the same value.
        if (currentRow?.cusipRef && String(currentRow.cusipRef).toLowerCase() === String(value).toLowerCase()) {
          updateRowData(index, { cusip: currentRow.cusipRef });
          return;
        }

        // Validate CUSIP when it's complete (9 chars)
        if (value.length >= 9) {
          setRowLoadingStates(prev => ({ ...prev, [index]: true }));
          
          try {
            const result = await validateCusip(token, value, index);
            if (!result.valid) {
              setRowErrors(prev => ({ ...prev, [index]: result.error }));
            } else {
              setRowErrors(prev => ({ ...prev, [index]: null }));
            }
          } catch (error) {
            setRowErrors(prev => ({ ...prev, [index]: 'Error validating CUSIP' }));
          } finally {
            setRowLoadingStates(prev => ({ ...prev, [index]: false }));
          }
        }
      } 
      // Clear quantity errors when value changes
      else if (field === 'quantity') {
        if (value && !isNaN(value) && Number(value) > 0) {
          setRowFieldErrors(prev => ({
            ...prev,
            [index]: {
              ...(prev[index] || {}),
              quantity: null
            }
          }));
        }
      } 
      // Clear price errors when value changes
      else if (field === 'price') {
        if (value && !isNaN(value) && Number(value) > 0) {
          setRowFieldErrors(prev => ({
            ...prev,
            [index]: {
              ...(prev[index] || {}),
              price: null
            }
          }));
        }
      }
    }
  };

  // Mark row as pending delete instead of actual delete
  const handleRowDelete = (index) => {
    const row = formData.rows[index];
    // In edit OR mapping mode, toggle pendingDelete for existing mapped rows
    if ((editMode || mappingMode) && row.accountPositionMappingId) {
      updateRowData(index, { pendingDelete: !row.pendingDelete });
    } else {
      // If it's a new row, just remove it directly
      removeRow(index);
      
      // Clean up any errors associated with this row
      setRowErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      
      setRowFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      
      setTouchedRowFields(prev => {
        const newTouched = { ...prev };
        delete newTouched[index];
        return newTouched;
      });
      
      // Re-index error states for rows that come after the deleted one
      const reindexedRowErrors = {};
      const reindexedRowFieldErrors = {};
      const reindexedTouchedRowFields = {};
      
      Object.keys(rowErrors).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex > index) {
          reindexedRowErrors[keyIndex - 1] = rowErrors[keyIndex];
        } else if (keyIndex < index) {
          reindexedRowErrors[keyIndex] = rowErrors[keyIndex];
        }
      });
      
      Object.keys(rowFieldErrors).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex > index) {
          reindexedRowFieldErrors[keyIndex - 1] = rowFieldErrors[keyIndex];
        } else if (keyIndex < index) {
          reindexedRowFieldErrors[keyIndex] = rowFieldErrors[keyIndex];
        }
      });
      
      Object.keys(touchedRowFields).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex > index) {
          reindexedTouchedRowFields[keyIndex - 1] = touchedRowFields[keyIndex];
        } else if (keyIndex < index) {
          reindexedTouchedRowFields[keyIndex] = touchedRowFields[keyIndex];
        }
      });
      
      setRowErrors(reindexedRowErrors);
      setRowFieldErrors(reindexedRowFieldErrors);
      setTouchedRowFields(reindexedTouchedRowFields);
    }
  };

  // Handle confirmed deletion of existing CUSIP
  const handleConfirmCusipDelete = async () => {
    if (cusipIndexToDelete === null || !cusipToDelete) return;
    
    const row = formData.rows[cusipIndexToDelete];
    setIsDeleting(true);
    
    try {
      // Call the API to delete the position
      if (row.accountPositionMappingId) {
        await deleteAccountPostionMapping(token, row.accountPositionMappingId);
        
        // After successful deletion, remove from originalCusips if it exists there
        if (originalCusips.has(cusipToDelete)) {
          setOriginalCusips(prev => {
            const updated = new Set(prev);
            updated.delete(cusipToDelete);
            return updated;
          });
        }
      }
      
      // Check if this is the last row
      const isLastRow = formData.rows.length === 1;
      
      if (isLastRow) {
        // If it's the last row, replace it with a completely empty new row
        updateRowData(cusipIndexToDelete, {
          cusip: '',
          cusipRef: '',
          secMasterId: null, 
          bondInventoryId: null,
          quantity: '',
          price: '',
          accountPositionMappingId: null
        });
      } else {
        // Remove the row from UI if it's not the last row
        removeRow(cusipIndexToDelete);
      }
      
      // Clean up associated errors
      setRowErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[cusipIndexToDelete];
        return newErrors;
      });
      
      setRowFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[cusipIndexToDelete];
        return newErrors;
      });
      
      setTouchedRowFields(prev => {
        const newTouched = { ...prev };
        delete newTouched[cusipIndexToDelete];
        return newTouched;
      });
      
      // Show success as snackbar instead of modal message
      useAccountPositionStore.getState().showNotification('Position deleted successfully');
    } catch (error) {
      // Set persistent error message
      setErrorMessage(error.message || 'Failed to delete position');
    } finally {
      setIsDeleting(false);
      setCusipDeleteConfirmOpen(false);
      setCusipToDelete(null);
      setCusipIndexToDelete(null);
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    const newRowErrors = {};
    
    // Basic field validation
    // if (!formData.pmId) newErrors.pmId = 'Please select a PM';
    if (!formData.accountGroup) {
      newErrors.accountGroup = 'Account identifier is required';
    } else {
      // In both modes, validate just the suffix length (without ACC-)
      if (formData.accountGroup.trim().length !== 6) {
        newErrors.accountGroup = 'Account identifier must be exactly 6 characters';
      }
    }
    if (!formData.custodianId) newErrors.custodianId = 'Please select a custodian';
    if (!safeToString(formData.description).trim()) newErrors.description = 'Description is required';
    
    // Description length validation
    if (safeToString(formData.description).length > 15) {
      newErrors.description = 'Description must be 15 characters or less';
    }
    
    // Validate each row individually
    let hasValidRow = false;
    formData.rows.forEach((row, index) => {
      // Check if the row is completely empty (all three fields empty)
      const isEmpty = 
        (!row.cusip || safeToString(row.cusip).trim() === '') && 
        (!row.quantity || safeToString(row.quantity).trim() === '') && 
        (!row.price || safeToString(row.price).trim() === '');
      
      // Skip validation for completely empty rows
      if (isEmpty) {
        return; // Continue to next iteration
      }

      // Skip validation for rows marked as pendingDelete
      if (row.pendingDelete) {
        return;
      }
      
      const rowErrors = {};
      
      // CUSIP validation
      if (!row.cusip || safeToString(row.cusip).length < 9) {
        rowErrors.cusip = 'CUSIP is required (min 9 characters)';
      } else if (!row.secMasterId && !(effectiveEdit && row.accountPositionMappingId)) {
        // Only require secMasterId for new rows. Existing rows in edit mode are exempt.
        rowErrors.cusip = 'Invalid CUSIP - not found in system';
      }
      
      // Quantity validation
      const quantityStr = safeToString(row.quantity).trim();
      if (quantityStr === '') {
        rowErrors.quantity = 'Quantity is required';
      } else if (isNaN(quantityStr)) {
        rowErrors.quantity = 'Quantity must be a number';
      } else if (Number(quantityStr) > MAX_QTY) {
        rowErrors.quantity = `Quantity must be ≤ ${MAX_QTY.toLocaleString()}`;
      } else if (Number(quantityStr) <= 0) {
        // Disallow negative and zero in both create and edit mode
        rowErrors.quantity = 'Quantity must be greater than 0';
      }
      
      // Price validation
      const priceStr = safeToString(row.price).trim();
      if (!priceStr || priceStr === '') {
        rowErrors.price = 'Price is required';
      } else if (isNaN(priceStr) || Number(priceStr) <= 0) {
        rowErrors.price = 'Price must be a positive number';
      } else if (Number(priceStr) > 150) {
        rowErrors.price = 'Price must be less than 150';
      }
      
      // If this row has no errors and all required fields are filled, it's valid
      const hasValidIdForMode = !!row.secMasterId || (effectiveEdit && !!row.accountPositionMappingId);
      if (Object.keys(rowErrors).length === 0 && row.cusip && hasValidIdForMode && quantityStr && priceStr) {
        hasValidRow = true;
      }
      
      // Store row errors if any
      if (Object.keys(rowErrors).length > 0) {
        newRowErrors[index] = rowErrors;
      }
    });
    
    // Check for duplicate CUSIPs among non-pendingDelete rows
    const activeRows = formData.rows.filter(row => !row.pendingDelete);
    const cusips = activeRows.map(row => row.cusip ? row.cusip.toUpperCase() : '').filter(cusip => cusip);
    const duplicates = cusips.filter((cusip, index, self) => 
      cusip && self.indexOf(cusip) !== index
    );
    if (duplicates.length > 0) {
      newErrors.duplicateCusips = `Duplicate CUSIP entries found: ${[...new Set(duplicates)].join(', ')}`;
    }

    // Update the error states
    setFieldErrors(newErrors);
    setRowFieldErrors(newRowErrors);

    // Mark all fields as touched for validation feedback
    const allFields = ['accountGroup', 'custodianId', 'description']; //removed pmid
    const newTouchedFields = {};
    allFields.forEach(field => {
      newTouchedFields[field] = true;
    });
    setTouchedFields(newTouchedFields);
    
    // Mark all row fields as touched (except completely empty rows)
    const newTouchedRowFields = {};
    formData.rows.forEach((row, rowIndex) => {
      const isEmpty = 
        (!row.cusip || safeToString(row.cusip).trim() === '') && 
        (!row.quantity || safeToString(row.quantity).trim() === '') && 
        (!row.price || safeToString(row.price).trim() === '');
      
      if (!isEmpty) {
        newTouchedRowFields[rowIndex] = {
          cusip: true,
          quantity: true,
          price: true
        };
      }
    });
    setTouchedRowFields(newTouchedRowFields);
    
    return Object.keys(newErrors).length === 0 && Object.keys(newRowErrors).length === 0;
  };

  // Helper function to check if a row is empty
  const isEmptyRow = (row) => {
    return (!row.cusip || safeToString(row.cusip).trim() === '') && 
           (!row.quantity || safeToString(row.quantity).trim() === '') && 
           (!row.price || safeToString(row.price).trim() === '');
  };

  // Helper function to safely convert value to string
  const safeToString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  // Check if there have been meaningful changes to the form
  const hasFormChanges = () => {
    // In create mode (not edit or mapping), any data is a change
    if (!effectiveEdit) return true;

    // In edit/mapping mode, need original data to compare
    if (!originalFormData) return false;

    // Enable update if any row is marked for deletion
    if ((formData.rows || []).some(row => row.pendingDelete)) {
      return true;
    }

    // Check basic field changes
    if (formData.description !== originalFormData.description ||
        formData.custodianId !== originalFormData.custodianId) {
      return true;
    }
    
    // Filter out empty rows for comparison
    const currentNonEmptyRows = (formData.rows || []).filter(row => !isEmptyRow(row));
    const originalNonEmptyRows = (originalFormData.rows || []).filter(row => !isEmptyRow(row));
    
    // If the number of non-empty rows has changed, there's a change
    if (currentNonEmptyRows.length !== originalNonEmptyRows.length) {
      return true;
    }
    
    // Check if there are new CUSIPs that weren't in the original data
    const originalCusipSet = new Set(originalNonEmptyRows.map(row => row.cusip));
    const hasNewCusips = currentNonEmptyRows.some(row => !originalCusipSet.has(row.cusip));
    if (hasNewCusips) {
      return true;
    }
    
    // Check each non-empty row for changes
    return currentNonEmptyRows.some(currentRow => {
      // Find matching row in original data by CUSIP
      const originalRow = originalNonEmptyRows.find(r => r.cusip === currentRow.cusip);
      
      // If no matching row, this is a new CUSIP
      if (!originalRow) return true;
      
      // Safely compare quantity and price as strings, handling null/undefined values
      return safeToString(currentRow.quantity) !== safeToString(originalRow.quantity) ||
             safeToString(currentRow.price) !== safeToString(originalRow.price);
    });
  };

  // Check if there are any validation errors
  const hasValidationErrors = () => {
    // Check field errors
    const hasFieldErrors = Object.keys(fieldErrors).some(key => fieldErrors[key]);
    
    // Check row field errors
    const hasRowFieldErrors = Object.keys(rowFieldErrors).some(rowIndex => 
      Object.keys(rowFieldErrors[rowIndex] || {}).some(field => rowFieldErrors[rowIndex][field])
    );
    
    // Check row errors (CUSIP validation errors)
    const hasRowErrors = Object.keys(rowErrors).some(key => rowErrors[key]);
    
    // Check duplicate CUSIPs
    const hasDuplicates = duplicateCusips.size > 0;
    
    return hasFieldErrors || hasRowFieldErrors || hasRowErrors || hasDuplicates;
  };

  // Form submission handler
  const handleSave = async () => {
    // Clear all messages before validation
    clearAllMessages();
    
    // Clear duplicate CUSIP error before validation
    setFieldErrors(prev => ({ ...prev, duplicateCusips: null }));

    // NEW: Check if this is header-only edit mode (editing account details only, no positions)
    const isHeaderOnlyEdit = editMode && lockAccountFieldOnly && hidePositions;

    if (isHeaderOnlyEdit) {
      // Header-only edit: validate only header fields
      const newErrors = {};
      if (!formData.custodianId) newErrors.custodianId = 'Please select a custodian';
      if (!safeToString(formData.description).trim()) newErrors.description = 'Description is required';
      if (safeToString(formData.description).length > 15) {
        newErrors.description = 'Description must be 15 characters or less';
      }

      if (Object.keys(newErrors).length > 0) {
        setFieldErrors(newErrors);
        setTouchedFields(prev => ({
          ...prev,
          custodianId: true,
          description: true
        }));
        return;
      }

      // Call the update-account API with only header fields
      setIsSubmitting(true);
      try {
        await updateAccountOnly(token, formData.accountId, formData.description, formData.custodianId);
        useAccountPositionStore.getState().showNotification('Account updated successfully');
        handleClose();
      } catch (error) {
        setMessage('error', error.message || 'Failed to update account', 'api-error', true);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // ...existing validation for full edit/create flow...
    if (!validateForm()) {
      return;
    }
    
    // Store the current selected account before the operation
    const currentSelectedAccount = useAccountPositionStore.getState().selectedAccount;

    setIsSubmitting(true);
    try {
      if (effectiveEdit) {
        // EXACTLY like edit flow: delete pending rows first, then update
        const rowsToDelete = formData.rows
          .map((row, idx) => ({ ...row, idx }))
          .filter(row => row.pendingDelete && row.accountPositionMappingId);

        let deleteErrors = [];
        for (const row of rowsToDelete) {
          try {
            await deleteAccountPostionMapping(token, row.accountPositionMappingId);
          } catch (err) {
            deleteErrors.push({ cusip: row.cusip, error: err.message || 'Delete failed' });
          }
        }
        if (deleteErrors.length > 0) {
          const errorMsg = 'Failed to delete the following CUSIPs: ' +
            deleteErrors.map(e => `${e.cusip} (${e.error})`).join(', ');
          setMessage('error', errorMsg, 'api-error', true);
          return;
        }

        const filteredRows = formData.rows.filter(row => !row.pendingDelete);
        const filteredOriginalPositions = originalPositions.filter(
          pos => !rowsToDelete.some(row => row.cusip === pos.cusip)
        );

        updateFormData({ rows: filteredRows });
        const result = await updateDemoAccount(token, filteredOriginalPositions);

        // Use returned account.id consistently
        setSelectedAccount(result?.account?.id || currentSelectedAccount);

        // Refresh list after update (mapping/edit)
        await useAccountPositionStore.getState().fetchAccountData();
      } else {
        // Pure create flow
        const createdAccount = await createDemoAccount(token);
        setSelectedAccount(createdAccount.id);
        await useAccountPositionStore.getState().fetchAccountData();
      }
      
      // Close modal
      handleClose();
    } catch (error) {
      const errorMsg = error.message || `Failed to ${effectiveEdit ? 'update' : 'create'} demo account`;
      setMessage('error', errorMsg, 'api-error', true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to add a new row with proper error state initialization
  const handleAddRow = () => {
    addRow();
    // Clear errors for the new row
    const newRowIndex = formData.rows.length;
    setRowErrors(prev => ({ ...prev, [newRowIndex]: null }));
    setRowFieldErrors(prev => ({ ...prev, [newRowIndex]: null }));
  };

  // Replace individual message states with a unified message system
  const [messageState, setMessageState] = useState({
    type: null, // 'error' | 'info' | 'success'
    content: '',
    source: null, // 'account-check' | 'form-validation' | 'api-error' | etc.
    persistent: false // whether message should persist through input changes
  });

  // Generic message management functions
  const setMessage = (type, content, source = null, persistent = false) => {
    setMessageState({ type, content, source, persistent });
  };

  const clearMessage = (sourceFilter = null) => {
    setMessageState(prev => {
      // If no source filter, clear all non-persistent messages
      if (!sourceFilter) {
        return prev.persistent ? prev : { type: null, content: '', source: null, persistent: false };
      }
      // If source filter provided, only clear if source matches
      return prev.source === sourceFilter ? { type: null, content: '', source: null, persistent: false } : prev;
    });
  };

  const clearAllMessages = () => {
    setMessageState({ type: null, content: '', source: null, persistent: false });
  };

  // NEW: Determine if we're in positions-only edit mode
  const positionsEditMode = lockHeaderFields && !lockAccountFieldOnly && !hidePositions;

  return (
    <Modal 
      open={open} 
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or escape key
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          handleClose();
        }
      }}
      disableEscapeKeyDown={true}
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disablePortal={false}
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={headerStyle}>
          <Typography variant="h6" sx={{ color: tokens.colors.fontColour, fontWeight: 'bold' }}>
            {modalTitle || (effectiveEdit ? 'Edit Account Positions' : 'Create Demo Account')}
          </Typography>
          <IconButton 
            onClick={handleClose} 
            sx={{ color: tokens.colors.fontColour }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Generic message display */}
        {messageState.content && (
          <Box sx={{
            mx: 3,
            mt: 1,
            p: 1.5,
            backgroundColor: messageState.type === 'error' 
              ? withAlpha(tokens.palette.danger, 0.08)
              : messageState.type === 'info'
              ? withAlpha(tokens.palette.primary, 0.08)
              : withAlpha(tokens.palette.success, 0.08),
            border: messageState.type === 'error'
              ? `1px solid ${withAlpha(tokens.palette.danger, 0.3)}`
              : messageState.type === 'info'
              ? `1px solid ${withAlpha(tokens.palette.primary, 0.3)}`
              : `1px solid ${withAlpha(tokens.palette.success, 0.3)}`,
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <Typography
              variant="body2"
              sx={{
                color: messageState.type === 'error'
                  ? tokens.palette.danger
                  : messageState.type === 'info'
                  ? tokens.palette.primaryA90
                  : tokens.palette.success,
                fontWeight: 600,
                fontSize: '14px'
              }}
            >
              {messageState.content}
            </Typography>
          </Box>
        )}

        {/* Content - using the extracted form component */}
        <AddAccountFormContent
          formData={formData}
          accountIdentifiers={accountIdentifiers}
          custodians={custodians}
          fieldErrors={fieldErrors}
          rowErrors={rowErrors}
          rowFieldErrors={rowFieldErrors}
          duplicateCusips={duplicateCusips}
          touchedFields={touchedFields}
          touchedRowFields={touchedRowFields}
          rowLoadingStates={rowLoadingStates}
          isLoading={isSaving}
          errorMessage={messageState.type === 'error' ? messageState.content : ''}
          editMode={editMode}
          originalCusips={originalCusips}
          handleAccountChange={handleAccountChange}
          handleCustodianChange={handleCustodianChange}
          handleChange={handleChange}
          handleRowChange={handleRowChange}
          handleRowDelete={handleRowDelete}
          addRow={handleAddRow}
          markFieldAsTouched={markFieldAsTouched}
          markRowFieldAsTouched={markRowFieldAsTouched}
          isCheckingAccount={isCheckingAccount}
          isAccountIdentifierValidated={isAccountIdentifierValidated}
          mappingMode={mappingMode}
          lockHeaderFields={lockHeaderFields}
          hidePositions={hidePositions}
          lockAccountFieldOnly={lockAccountFieldOnly}
          positionsEditMode={positionsEditMode}
        />

        {/* Footer */}
        <Box sx={footerStyle}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isSaving}
            sx={{
              mr: 2,
              
              borderRadius: '8px',
              width: '150px',
              height: '46px',
              fontWeight: 700,
              textTransform: 'none',
              
              '&.Mui-disabled': {
                borderColor: tokens.colors.bodyBorderColor50,
                color: tokens.colors.disabledFontColour,
                backgroundColor: tokens.colors.disabledBackground,
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              isSaving ||
              hasValidationErrors() ||
              (editMode && !hasFormChanges()) ||
              accountIdentifierStatus === 'owned' ||
              (!effectiveEdit && !isAccountIdentifierValidated)
            }
            sx={{
              backgroundColor: tokens.palette.primary,
              color: tokens.colors.white,
              borderRadius: '8px',
              width: '150px',
              height: '46px',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { backgroundColor: tokens.palette.primaryA80 },
              '&:disabled': {
                backgroundColor: tokens.palette.primaryA25,
                color: tokens.colors.disabledFontColour,
              }
            }}
          >
            {isSaving ? <CircularProgress size={24} color="inherit" /> : (effectiveEdit ? 'Update' : 'Submit')}
          </Button>
        </Box>

        {/* Confirmation Dialog */}
        <ConfirmCusipDeleteDialog
          open={cusipDeleteConfirmOpen}
          onClose={() => {
            if (!isDeleting) {
              setCusipDeleteConfirmOpen(false);
              setCusipToDelete(null);
              setCusipIndexToDelete(null);
            }
          }}
          onConfirm={handleConfirmCusipDelete}
          cusip={cusipToDelete}
          isDeleting={isDeleting}
        />
      </Box>
    </Modal>
  );
};

export default AddAccountModal;